import React, { useRef, useState } from "react";
import { Box, Typography, Paper, Grid, Divider, styled, useTheme, Theme } from "@mui/material";
import { Value } from "components/Value";
// import { Row, Col } from "v2/components/base/Flex/Flex.hoc";
// import { RetroLoadingButton } from "v2/components/base/Button/Button.hoc";
import { TestSelect } from "v2/components/base/Select/Select.hoc";
// import { TestModal, TestModalContent, TestModalHeader } from "v2/components/base/Model/Model.ui";
// import { Scrollable } from "v2/components/base/Scrollable/Scrollable.hoc";
import { useInfiniteQuery } from "@tanstack/react-query";
import { DEFAULT_TRAITS } from "pages/v2/group";
import {
  DNA_CLASS_CONFIG_MAP,
  DNA_ATTRIBUTE_CONFIG_MAP,
  DNAAttribute,
  DNAClass,
  SCORE_LEVEL,
  MAX_TRAIT_SCORE,
} from "pages/cabal-id/dna/dnaConfig";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import axiosService from "services/axios";
import { Cabal } from "types";
import { CabalFreeGroupRowV2 } from "components/CabalGroup";
import { useInViewInfiniteQuery } from "hooks/useInViewInfiniteQuery";
import { useSelect } from "v2/components/base/Select/hooks/useSelect";
import { useModel } from "v2/components/base/Model/hooks/useModel";
import { Card, Row, Col } from "v2/components/base/Flex/ui";
import { BaseButton, CircleButton } from "v2/components/base/Button/ui";
import RootLayout from "v2/layouts/RootLayout";
import TestModel from "v2/components/Models/TestModel";
import BasicButton from "v2/components/Buttons/BasicButton";
import Scrollable from "v2/components/base/Scrollable/ui";
import GroupRowItem from "v2/components/ScrollableItem/GroupRowItem/ui";
import Select from "v2/components/base/Select/ui";
import { PercentageBlock } from "v2/components/NumBlock/ui";

const languageSelectOptions = [
  { label: "Languages", value: "all" },
  {
    label: "English",
    value: "en",
  },
  {
    label: "한국어",
    value: "ko",
  },
  {
    label: "中文",
    value: "zh",
  },
  {
    label: "русский",
    value: "ru",
  },
  {
    label: "Tiếng Việt",
    value: "vi",
  },
];

// Styled components for the demo page
const Section = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  backgroundColor: theme.palette.surface.container,
  border: `1px solid ${theme.palette.border.dark}`,
}));

const ColorBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "bgcolor" && prop !== "color",
})<{ bgcolor?: string; color?: string }>(({ bgcolor, color }) => ({
  width: "100%",
  height: "80px",
  backgroundColor: bgcolor,
  color: color || "inherit",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "8px",
  borderRadius: "4px",
  marginBottom: "8px",
}));

const SampleText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "textcolor",
})<{ textcolor?: string }>(({ textcolor }) => ({
  color: textcolor,
  marginBottom: "8px",
}));

const DesignSystemPage = () => {
  const theme = useTheme();
  const { open, isOpen, close } = useModel({
    modelId: "test-model",
  });
  const scrollableRef = useRef<HTMLDivElement>(null);
  /* Filters */
  const [selectedClasses, setSelectedClasses] = useState<Map<DNAClass, boolean>>(
    localStorage.getItem("selectedClasses")
      ? new Map(JSON.parse(localStorage.getItem("selectedClasses")!))
      : new Map(),
  );
  const [selectedTraits, setSelectedTraits] = useState<Map<DNAAttribute, number[]>>(
    localStorage.getItem("selectedTraits")
      ? new Map(JSON.parse(localStorage.getItem("selectedTraits")!))
      : new Map(DEFAULT_TRAITS),
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem("selectedLanguage")
      ? localStorage.getItem("selectedLanguage")
      : languageSelectOptions[0].value,
  );
  const { tgUserId, walletAddresses = {} } = useUserStoreV2();
  const { tonAddress } = walletAddresses;

  const [page, setPage] = useState(1);
  const queryResult = useInfiniteQuery({
    queryKey: [
      "tg-groups",
      {
        page,
        language: selectedLanguage ?? "all",
        name: "",
        groupType: "all",
        pricing: "all",
        mode: "all",
        ...(selectedClasses.size > 0
          ? { classNames: Array.from(selectedClasses.keys()).join(",") }
          : {}),
        ...(selectedTraits.size > 0
          ? Array.from(selectedTraits.keys()).reduce((acc, key) => {
              const value = selectedTraits.get(key as DNAAttribute);
              if (value) {
                const min =
                  value[0] === 5
                    ? MAX_TRAIT_SCORE
                    : SCORE_LEVEL[
                        ["VERY_LOW", "LOW", "MEDIUM", "HIGH", "VERY_HIGH"][
                          value[0]
                        ] as keyof typeof SCORE_LEVEL
                      ]!.toString();
                const max =
                  value[1] === 5
                    ? MAX_TRAIT_SCORE
                    : SCORE_LEVEL[
                        ["VERY_LOW", "LOW", "MEDIUM", "HIGH", "VERY_HIGH"][
                          value[1]
                        ] as keyof typeof SCORE_LEVEL
                      ]!.toString();
                acc[key] = `${min}:${max}`;
              }
              return acc;
            }, {} as Record<DNAAttribute, string>)
          : {}),
      },
    ],
    queryFn: async ({ pageParam }) => {
      return axiosService.getTelegramGroups({
        filter: {
          pricing: "all",
          language: selectedLanguage ?? "all",
          groupType: "all",
          name: "",
          tgUserId: tgUserId?.toString(),
          tonAddress,
          ...(selectedClasses.size > 0
            ? { classNames: Array.from(selectedClasses.keys()).join(",") }
            : {}),
          ...(selectedTraits.size > 0
            ? Array.from(selectedTraits.keys()).reduce((acc, key) => {
                const value = selectedTraits.get(key as DNAAttribute);
                if (value) {
                  const min =
                    value[0] === 5
                      ? MAX_TRAIT_SCORE
                      : SCORE_LEVEL[
                          ["VERY_LOW", "LOW", "MEDIUM", "HIGH", "VERY_HIGH"][
                            value[0]
                          ] as keyof typeof SCORE_LEVEL
                        ]!.toString();
                  const max =
                    value[1] === 5
                      ? MAX_TRAIT_SCORE
                      : SCORE_LEVEL[
                          ["VERY_LOW", "LOW", "MEDIUM", "HIGH", "VERY_HIGH"][
                            value[1]
                          ] as keyof typeof SCORE_LEVEL
                        ]!.toString();
                  acc[key] = `${min}:${max}`;
                }
                return acc;
              }, {} as Record<DNAAttribute, string>)
            : {}),
        },
        selects: [],
        limit: 10,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage.page + 1;
    },

    getPreviousPageParam: (firstPage, pages) => {
      return firstPage.page - 1;
    },
    initialPageParam: 1,
  });
  const { data, isLoading, isSuccess } = queryResult;
  const { ref, hasAllFetched } = useInViewInfiniteQuery(queryResult);
  const { value, setValue, defaultValue, handleOnChange, options, reset } = useSelect({
    options: languageSelectOptions,
    defaultValue: languageSelectOptions[0].value,
    onChange: (value) => {
      console.log(value);
    },
    onReset: () => {
      console.log("reset");
    },
  });

  const handleCabalClick = () => {
    if (scrollableRef.current) {
      localStorage.setItem("leaderboardHeight", scrollableRef.current.scrollTop.toString());
    }
  };

  // Typography variants from the theme
  const typographyVariants = [
    "display-lg",
    "display-md",
    "display-sm",
    "title-h1",
    "title-h2",
    "title-h3",
    "subtitle",
    "body-lg",
    "body-md",
    "body-default",
    "body-sm",
    "pre-title",
    "pre-title-2",
    "button-lg",
    "button-md",
    "button-sm",
  ];

  // Text colors from the theme
  const textColors = {
    "Text Primary": theme.palette.text.primary,
    "Text Secondary": theme.palette.text.secondary,
    "Text Brand": theme.palette.text.brand,
    "Text Disabled": theme.palette.text.disabled,
    "Text System Green": theme.palette.text["system-green"],
    "Text System Red": theme.palette.text["system-red"],
    "Text Button Dark": theme.palette.text["btn-dark"],
  };

  // Background colors from the theme
  const backgroundColors = {
    // Brand
    "Brand Primary": theme.palette.brand.primary,
    "Brand Primary BG": theme.palette.brand["primary-bg"],

    // System
    "System Green": theme.palette.system.green,
    "System Green BG": theme.palette.system["green-bg"],
    "System Red": theme.palette.system.red,
    "System Red BG": theme.palette.system["red-bg"],

    // Surface
    "Surface Light": theme.palette.surface.light,
    "Surface Light BG": theme.palette.surface["light-bg"],
    "Surface Silver": theme.palette.surface.silver,
    "Surface Container": theme.palette.surface.container,
    "Surface BG": theme.palette.surface.bg,
    "Surface Container BG": theme.palette.surface["container-bg"],

    // Borders
    "Border Dark": theme.palette.border.dark,
    "Border Light": theme.palette.border.light,
  };

  return (
    <>
      <Box sx={{ maxWidth: "1200px", margin: "0 auto", padding: 3 }}>
        <Typography variant="title-h1" component="h1" gutterBottom>
          Design System
        </Typography>

        {/* Typography Section */}
        <Section>
          <Typography variant="title-h2" gutterBottom>
            Typography
          </Typography>
          <Divider sx={{ mb: 4 }} />

          <Grid container spacing={2}>
            {typographyVariants.map((variant) => (
              <Grid item xs={12} md={6} key={variant}>
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    border: `1px solid ${theme.palette.border.light}`,
                    borderRadius: "4px",
                  }}>
                  <Typography
                    variant="body-sm"
                    component="div"
                    gutterBottom
                    sx={{ color: theme.palette.text.secondary }}>
                    {variant}
                  </Typography>
                  <Typography variant={variant as any} component="div">
                    This is {variant} text
                  </Typography>
                  <Typography
                    variant="body-sm"
                    component="div"
                    sx={{ mt: 1, color: theme.palette.text.secondary }}>
                    {`Font: ${(theme.typography as any)[variant].fontFamily}`}
                    <br />
                    {`Size: ${(theme.typography as any)[variant].fontSize}`}
                    <br />
                    {`Weight: ${(theme.typography as any)[variant].fontWeight}`}
                    <br />
                    {`Line Height: ${(theme.typography as any)[variant].lineHeight}`}
                    {(theme.typography as any)[variant].letterSpacing && (
                      <>
                        <br />
                        {`Letter Spacing: ${(theme.typography as any)[variant].letterSpacing}`}
                      </>
                    )}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Section>

        {/* Text Colors Section */}
        <Section>
          <Typography variant="title-h2" gutterBottom>
            Text Colors
          </Typography>
          <Divider sx={{ mb: 4 }} />

          <Grid container spacing={3}>
            {Object.entries(textColors).map(([name, color]) => (
              <Grid item xs={12} sm={6} md={4} key={name}>
                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.border.light}`,
                    borderRadius: "4px",
                  }}>
                  <SampleText variant="body-md" textcolor={color}>
                    {name}
                  </SampleText>
                  <SampleText variant="body-sm" textcolor={theme.palette.text.secondary}>
                    {color as string}
                  </SampleText>
                  <Box
                    sx={{
                      height: "40px",
                      backgroundColor: color,
                      borderRadius: "4px",
                      border: `1px solid ${theme.palette.border.light}`,
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Section>

        {/* Background Colors Section */}
        <Section>
          <Typography variant="title-h2" gutterBottom>
            Background Colors
          </Typography>
          <Divider sx={{ mb: 4 }} />

          <Grid container spacing={3}>
            {Object.entries(backgroundColors).map(([name, color]) => (
              <Grid item xs={12} sm={6} md={4} key={name}>
                <ColorBox
                  bgcolor={color}
                  color={
                    name.includes("Light")
                      ? theme.palette.text["btn-dark"]
                      : theme.palette.text.primary
                  }>
                  {name}
                </ColorBox>
                <Typography variant="body-sm" sx={{ color: theme.palette.text.secondary }}>
                  {color as string}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Section>

        {/* Font Preview Section */}
        <Section>
          <Typography variant="title-h2" gutterBottom>
            Font Preview
          </Typography>
          <Divider sx={{ mb: 4 }} />

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="title-h3" gutterBottom>
                  TT Interphases Pro Mono
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.border.light}`,
                    borderRadius: "4px",
                  }}>
                  <Typography
                    sx={{
                      fontFamily: "TT Interphases Pro Mono, sans-serif",
                      fontWeight: "normal",
                    }}>
                    Regular - The quick brown fox jumps over the lazy dog. 1234567890
                  </Typography>
                  <Typography
                    sx={{ fontFamily: "TT Interphases Pro Mono, sans-serif", fontWeight: "bold" }}>
                    Bold - The quick brown fox jumps over the lazy dog. 1234567890
                  </Typography>
                  <Typography
                    sx={{ fontFamily: "TT Interphases Pro Mono, sans-serif", fontStyle: "italic" }}>
                    Italic - The quick brown fox jumps over the lazy dog. 1234567890
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "TT Interphases Pro Mono, sans-serif",
                      fontWeight: "bold",
                      fontStyle: "italic",
                    }}>
                    Bold Italic - The quick brown fox jumps over the lazy dog. 1234567890
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="title-h3" gutterBottom>
                  Sora Font
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.border.light}`,
                    borderRadius: "4px",
                  }}>
                  <Typography sx={{ fontFamily: "Sora Font, sans-serif", fontWeight: "normal" }}>
                    Regular - The quick brown fox jumps over the lazy dog. 1234567890
                  </Typography>
                  <Typography sx={{ fontFamily: "Sora Font, sans-serif", fontWeight: "bold" }}>
                    Bold - The quick brown fox jumps over the lazy dog. 1234567890
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="title-h3" gutterBottom>
                  Cabin Condensed
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.border.light}`,
                    borderRadius: "4px",
                  }}>
                  <Typography sx={{ fontFamily: "Cabin Condensed, sans-serif", fontWeight: "400" }}>
                    Regular - The quick brown fox jumps over the lazy dog. 1234567890
                  </Typography>
                  <Typography sx={{ fontFamily: "Cabin Condensed, sans-serif", fontWeight: "500" }}>
                    Medium - The quick brown fox jumps over the lazy dog. 1234567890
                  </Typography>
                  <Typography sx={{ fontFamily: "Cabin Condensed, sans-serif", fontWeight: "600" }}>
                    Semi-Bold - The quick brown fox jumps over the lazy dog. 1234567890
                  </Typography>
                  <Typography sx={{ fontFamily: "Cabin Condensed, sans-serif", fontWeight: "700" }}>
                    Bold - The quick brown fox jumps over the lazy dog. 1234567890
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="title-h3" gutterBottom>
                  Aber Mono
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.border.light}`,
                    borderRadius: "4px",
                  }}>
                  <Typography sx={{ fontFamily: "Aber Mono, sans-serif", fontWeight: "300" }}>
                    Light - The quick brown fox jumps over the lazy dog. 1234567890
                  </Typography>
                  <Typography sx={{ fontFamily: "Aber Mono, sans-serif", fontWeight: "normal" }}>
                    Regular - The quick brown fox jumps over the lazy dog. 1234567890
                  </Typography>
                  <Typography sx={{ fontFamily: "Aber Mono, sans-serif", fontWeight: "bold" }}>
                    Bold - The quick brown fox jumps over the lazy dog. 1234567890
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Section>
        <Section>
          <Typography variant="title-h2" gutterBottom>
            Price
          </Typography>
          <Divider sx={{ mb: 4 }} />

          <Value value={0.000012} />
          <Value value={0.00012} />
          <Value value={0.0012} />
          <Value value={0.012} />
          <Value value={0.12} />
          <Value value={1} />
          <Value value={10} />
          <Value value={100} />
          <Value value={1000} />
          <Value value={10000} />
          <Value value={1.1} />
          <Value value={11} />
          <Value value={110} />
          <Value value={1100} />
          <Value value={11000} />
          <Value value={0.00001256124125} />
          <Value value={0.0001256124125} />
          <Value value={0.001256124125} />
          <Value value={0.01256124125} />
          <Value value={0.1256124125} />
          <Value value={1.256124125} />
          <Value value={12.56124125} />
          <Value value={125.6124125} />
          <Value value={1256.124125} />
          <Value value={12561.24125} />
          <Value value={125612.4125} />
          <Value value={1256124.125} />
          <Value value={12561241.25} />
          <Value value={125612412.5} />
          <Value value={1256124125} />
        </Section>
        <Section>
          {/* <Row type="bc" fullWidth>
          <Col type="ss" fullWidth>
            <Typography variant="title-h2" gutterBottom>
              Col-1-ss
            </Typography>
            <Row type="cc" fullWidth>
              Row-1-cc
            </Row>
            <Row type="ee" fullWidth>
              Row-2-ee
            </Row>
          </Col>
          <Col type="cc" fullWidth>
            <Typography variant="title-h2" gutterBottom>
              Col-2-cc
            </Typography>
            <Row type="cc" fullWidth>
              Row-1-cc
            </Row>
            <Row type="ee" fullWidth>
              Row-2-ee
            </Row>
          </Col>
        </Row> */}
        </Section>
        <Section>
          {/* <RetroLoadingButton
          variant="primary"
          // isLoading={true}
          onClick={open}
        >
          Button
        </RetroLoadingButton> */}
          <PercentageBlock percentage={0.12} />
          <PercentageBlock version="v1" percentage={0.12} />
          <TestSelect
            defaultValue={defaultValue}
            value={value}
            onChange={handleOnChange}
            options={languageSelectOptions}
          />
          <Select
            // sx={{ width: "fit-content" }}
            defaultValue={defaultValue}
            value={value}
            onChange={handleOnChange}
            options={languageSelectOptions}
          />
          {/* <TestModal open={isOpen} onClose={close}>
          <TestModalContent>
            <TestModalHeader close={close} />
          </TestModalContent>
        </TestModal> */}
          <CircleButton>V2 Circle Button</CircleButton>
          <Card.ImageSection imageUrl="/logo.png" width="100%" type="fixed-aspect">
            12
          </Card.ImageSection>
          <BaseButton version="v1">V1 Button</BaseButton>
          <BaseButton>V2 Button</BaseButton>
          <Card.Section>
            <Card.Item>
              <Typography variant="title-h2">123123</Typography>
            </Card.Item>
          </Card.Section>
          <Scrollable
            scrollRef={scrollableRef}
            isLoading={isLoading}
            isEmpty={data?.pages[0].totalResults === 0}
            hasAllFetched={hasAllFetched}
            loadMoreRef={ref}>
            {data?.pages
              .flatMap((page) => page.results)
              .map((group: any, index: number) => {
                // if (!group.masterAddress) {
                return (
                  <Box sx={{ width: "100%" }} key={group._id}>
                    <GroupRowItem
                      cabal={group as Cabal}
                      rank={index + 1}
                      onClick={handleCabalClick}
                    />
                  </Box>
                );
              })}
          </Scrollable>
        </Section>
        <Section>
          <Row>
            <Typography variant="mobile-h1">V2 Typography</Typography>
          </Row>
          <Divider sx={{ mb: 4 }} />
          <Col type="cs" sx={{ gap: "12px" }}>
            <Row>
              <Typography variant="mobile-display-m">mobile-display-m</Typography>
            </Row>
            <Divider sx={{ mb: 1 }} />
            <Row>
              <Typography variant="mobile-display-s">mobile-display-s</Typography>
            </Row>
            <Divider sx={{ mb: 1 }} />
            <Row>
              <Typography variant="mobile-display-xs">mobile-display-xs</Typography>
            </Row>
            <Row>
              <Typography variant="mobile-h1">mobile-h1</Typography>
            </Row>
            <Row>
              <Typography variant="mobile-h2">mobile-h2</Typography>
            </Row>
            <Row>
              <Typography variant="mobile-h3">mobile-h3</Typography>
            </Row>
            <Row>
              <Typography variant="mobile-body-m">mobile-body-m</Typography>
            </Row>
            <Row>
              <Typography variant="mobile-body-s">mobile-body-s</Typography>
            </Row>
            <Row>
              <Typography variant="mobile-body-xs">mobile-body-xs</Typography>
            </Row>
            <Row>
              <Typography variant="mobile-body-m-heavy">mobile-body-m-heavy</Typography>
            </Row>
            <Row>
              <Typography variant="mobile-body-s-heavy">mobile-body-s-heavy</Typography>
            </Row>
            <Row>
              <Typography variant="mobile-action-s">mobile-action-s</Typography>
            </Row>
            <Row>
              <Typography variant="mobile-action-s-heavy">mobile-action-s-heavy</Typography>
            </Row>
            <Row>
              <Typography variant="mobile-action-xs-heavy">mobile-action-xs-heavy</Typography>
            </Row>
          </Col>
        </Section>
        <Section>
          <BasicButton onClick={open}>Share</BasicButton>
        </Section>
      </Box>
      <TestModel open={isOpen} onClose={close} />
    </>
  );
};

export default DesignSystemPage;
